import { useCallback, useMemo } from 'react';

import { useAreaData } from './useAreaData.js';
import { useBarData } from './useBarData.js';
import { useBuildStrategy } from './useBuildStrategy.js';
import { useDotData } from './useDotData.js';
import { useLineData } from './useLineData.js';
import { useXYChartData } from './useXYChartData.js';
import { useXYChartScales } from './useXYChartScales.js';
import { useXYChartTooltip } from './useXYChartTooltip.js';
import type { SeriesToQuadtree } from '../context/XYChartOverlayPerformance.context.js';
import {
  buildGetBarPixelCenter,
  buildGetLinearSeriesPixelCenter,
  buildSortedEntries,
  type GetBarPixelCenter,
  type GetLinearSeriesPixelCenter,
  type SortedEntriesPerShape,
} from '../utils/build-sorted-entries.js';
import { buildQuadtrees } from '../utils/query-closest-quadtree.js';

type OverlayBuildResult = SortedEntriesPerShape | SeriesToQuadtree;

export type UseBuildOverlayStrategyResult = {
  buildResult: OverlayBuildResult | undefined;
  getBarPixelCenter: GetBarPixelCenter;
  getLinearSeriesPixelCenter: GetLinearSeriesPixelCenter;
};

/**
 * Builds the overlay query structure on a debounced schedule (via
 * `useBuildStrategy`). Chooses between sorted entries (shared tooltip) and
 * quadtrees (single tooltip) based on the active variant, and exposes the
 * stable `getBarPixelCenter` accessor needed by both query builders.
 */
export function useBuildOverlayStrategy(): UseBuildOverlayStrategyResult {
  const axisIdToScales = useXYChartScales();
  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();
  const { variant } = useXYChartTooltip();
  const isSharedVariant = variant === 'shared';

  const { filteredSeriesWithDatapoints: barSeriesData } = useBarData();
  const { filteredSeriesWithDatapoints: dotSeriesData } = useDotData();
  const { filteredSeriesWithDatapoints: lineSeriesData } = useLineData();
  const { filteredSeriesWithDatapoints: areaSeriesData } = useAreaData();

  const getBarPixelCenter = useMemo(
    () =>
      buildGetBarPixelCenter(
        axisIdToScales,
        seriesToMetadata,
        axisIdToScaleDomain,
        datapointMetadataMap,
      ),
    [
      axisIdToScales,
      seriesToMetadata,
      axisIdToScaleDomain,
      datapointMetadataMap,
    ],
  );

  const getLinearSeriesPixelCenter = useMemo(
    () =>
      buildGetLinearSeriesPixelCenter(
        axisIdToScales,
        seriesToMetadata,
        axisIdToScaleDomain,
        datapointMetadataMap,
      ),
    [
      axisIdToScales,
      seriesToMetadata,
      axisIdToScaleDomain,
      datapointMetadataMap,
    ],
  );

  const build = useCallback(
    () =>
      isSharedVariant
        ? buildSortedEntries(
            barSeriesData,
            lineSeriesData,
            areaSeriesData,
            datapointMetadataMap,
            getBarPixelCenter,
            getLinearSeriesPixelCenter,
          )
        : buildQuadtrees(
            [...dotSeriesData, ...lineSeriesData, ...areaSeriesData],
            axisIdToScales,
            datapointMetadataMap,
            seriesToMetadata,
            axisIdToScaleDomain,
          ),
    [
      isSharedVariant,
      barSeriesData,
      lineSeriesData,
      areaSeriesData,
      datapointMetadataMap,
      getBarPixelCenter,
      getLinearSeriesPixelCenter,
      dotSeriesData,
      axisIdToScales,
      seriesToMetadata,
      axisIdToScaleDomain,
    ],
  );

  const buildResult = useBuildStrategy<OverlayBuildResult>(build);

  return { buildResult, getBarPixelCenter, getLinearSeriesPixelCenter };
}
